import React, { useMemo } from "react";
import type { DocumentElement as DocumentElementType } from "@eaco/forms-core";

import { useDocument } from "../DocumentView/DocumentContext";
import PageRichText from "./PageRichText";
import { usePageTextBlock } from "./usePageTextBlock";

interface Props {
  element: DocumentElementType;
}

const NUMBERED_TYPE = "page-numbered-list";

/** The array of siblings that directly contains `id` (searches nested pages). */
const findSiblings = (
  elements: DocumentElementType[],
  id: string,
): DocumentElementType[] | null => {
  if (elements.some((e) => e.id === id)) return elements;
  for (const el of elements) {
    const kids = el.elements as DocumentElementType[] | undefined;
    if (kids) {
      const found = findSiblings(kids, id);
      if (found) return found;
    }
  }
  return null;
};

/** A block's indent level (0 when unset). */
const readIndent = (el: DocumentElementType): number => {
  const v = (el.data as { indent?: unknown } | undefined)?.indent;
  return typeof v === "number" && v > 0 ? Math.floor(v) : 0;
};

/**
 * A numbered-list item. One element per item; the ordinal is derived from
 * position rather than stored.
 *
 * Counting follows a word processor: only items at this item's own indent
 * level count, so a nested sub-list has its own 1, 2, 3 and the outer list
 * carries on where it left off underneath. Walking back from this item,
 * deeper blocks are skipped over, anything shallower ends the sequence (we
 * have left this sub-list), and a non-numbered block at the same level
 * restarts the count.
 */
const PageNumberedList: React.FC<Props> = ({ element }) => {
  const { document } = useDocument();
  const { richTextProps } = usePageTextBlock(element);

  const ordinal = useMemo(() => {
    const siblings =
      findSiblings(document.elements as DocumentElementType[], element.id) ?? [];
    const idx = siblings.findIndex((e) => e.id === element.id);
    if (idx === -1) return 1;
    const level = readIndent(siblings[idx]);
    let n = 1;
    for (let i = idx - 1; i >= 0; i--) {
      const depth = readIndent(siblings[i]);
      // Nested content belongs to an item above; step over it.
      if (depth > level) continue;
      // Back out to a shallower block: this sub-list starts here.
      if (depth < level) break;
      if (siblings[i].typeId !== NUMBERED_TYPE) break;
      n++;
    }
    return n;
  }, [document, element.id]);

  return (
    <div className="flex gap-[11px]">
      <span
        aria-hidden
        className="min-w-[1.4em] flex-none select-none text-right font-jakarta text-[16px] font-medium leading-[1.68] tabular-nums text-[#8f8b83]"
      >
        {ordinal}.
      </span>
      <div className="min-w-0 flex-1">
        <PageRichText {...richTextProps} />
      </div>
    </div>
  );
};

export default PageNumberedList;
